import axios, { AxiosRequestConfig } from "axios";
import to from "await-to-js";
import qs from "qs";
import env from "../../../src/config/env";
import { JSCallAPP } from "./interactive";
import { Toast } from "vant";
import router from "@/router/index";
import { useMainStore } from "@/store/main";

const service = axios.create({
  baseURL: env.baseUrl,
  timeout: 5 * 1000 * 60,
});

let token: string | null = null;

// request interceptor
service.interceptors.request.use(
  (config) => {
    const store = useMainStore();

    // 处理公共 token
    // token 从地址栏的 token 参数中获取
    if (!(config as RequestConfig).notNeedToken) {
      if (token === null) {
        token = store.token || (router.currentRoute.query.token as string);
      }
      config.headers["Authorization"] = token;
    }

    // 处理 get 请求的 data 参数
    if (config.method === "get" && config.data) {
      config.url += "?" + qs.stringify(config.data);
    }

    // 处理 post 类型为 application/x-www-form-urlencoded 的参数
    if (
      config.method === "post" &&
      config.headers["content-type"] === "application/x-www-form-urlencoded" &&
      config.data
    ) {
      config.data = qs.stringify(config.data);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// response interceptor
service.interceptors.response.use(
  (response) => {
    const { data, config } = response;
    const { code } = data;

    if (code !== 200) {
      const msg = data.message || "请求错误!";

      if (
        // token 不能为空
        code === 10011 ||
        // refreshToken 过期
        code === 10013 ||
        // token 被撤销(已失效)
        code === 10014 ||
        // token 异常(解析异常)
        code === 10015 ||
        // token 过期
        code === 10012
      ) {
        // token 失败回调 APP
        JSCallAPP({
          method: "token_invalidation",
          data: {
            code,
            message: msg,
          },
        });
        console.log("token 失效", code, msg);
      } else {
        Toast(msg);
      }
      // 错误处理
      return Promise.reject(new Error(data.message || "请求错误!"));
    } else {
      return (config as RequestConfig).wholeResponse ? data : data.data;
    }
  },
  (error) => {
    if (process.env.NODE_ENV === "development") {
      console.error(error);
    }
    return Promise.reject(error);
  }
);

// -------------------------------------------------
//            ↓ 请求上层封装 ↓
// -------------------------------------------------

// 扩展 axios 请求配置
type RequestConfig = AxiosRequestConfig & {
  // 请求地址，不带 baseURL
  url: string;

  // 返回值是否保留外层参数，如返回数据为 { code: 1, data: {}}
  // 默认返回的是 data, 如果配置 wholeResponse 则保留包含 code 和 data
  wholeResponse?: boolean | undefined;

  // 是否需要签名
  notNeedToken?: boolean | undefined;
};

// 配置 wholeResponse 时的响应包装

type ResData = Record<string, never>;
type WholeResponse<T = ResData> = {
  code: number;
  message: string;
  data: T;
};
type WholeRequestConfig = RequestConfig & { wholeResponse: true };
type WholeResponsePromise<T> = Promise<
  [Error, undefined] | [null, WholeResponse<T>]
>;
type ResponsePromise<T> = Promise<[Error, undefined] | [null, T]>;

// get
function _get<T = ResData>(config: WholeRequestConfig): WholeResponsePromise<T>;
function _get<T = ResData>(config: RequestConfig): ResponsePromise<T>;
function _get<T = ResData>(config: RequestConfig): ResponsePromise<T> {
  return to<T>(
    service.request({
      method: "get",
      ...config,
    })
  );
}
export const get = _get;

// post json
function _postJSON<T = ResData>(
  config: WholeRequestConfig
): WholeResponsePromise<T>;
function _postJSON<T = ResData>(config: RequestConfig): ResponsePromise<T>;
function _postJSON<T = ResData>(config: RequestConfig): ResponsePromise<T> {
  return to<T>(
    service.request({
      method: "post",
      headers: {
        "content-type": "application/json",
      },
      ...config,
    })
  );
}
export const postJson = _postJSON;

// post form
function _postForm<T = ResData>(
  config: WholeRequestConfig
): WholeResponsePromise<T>;
function _postForm<T = ResData>(config: RequestConfig): ResponsePromise<T>;
function _postForm<T = ResData>(config: RequestConfig): ResponsePromise<T> {
  return to<T>(
    service.request({
      method: "post",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      ...config,
    })
  );
}
export const postForm = _postForm;

// post formData
function _postFormData<T = ResData>(
  config: WholeRequestConfig
): WholeResponsePromise<T>;
function _postFormData<T = ResData>(config: RequestConfig): ResponsePromise<T>;
function _postFormData<T = ResData>(config: RequestConfig): ResponsePromise<T> {
  return to<T>(
    service.request({
      method: "post",
      headers: {
        "content-type": "multipart/form-data",
      },
      ...config,
    })
  );
}
export const postFormData = _postFormData;
